import { type UniversalCreateMenuSection } from '@atlassian/universal-create/types';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useUserHasCapabilities } from '@townsquare/capability-provider/useUserHasCapabilities';
import { filterUndefined } from '@townsquare/filter-type';
import { useUniversalCreateItems } from '@townsquare/navigation/universal-create/items';
import { type UseUniversalCreateItemsProps } from '@townsquare/navigation/universal-create/types';
import { useNoWorkspaceExperience } from '@townsquare/workspace-store';

// note: consider moving this into an app context if usage of displaying kudos display increases
function useShowKudos() {
	const noWorkspace = useNoWorkspaceExperience();
	if (noWorkspace) {
		return false;
	}
	return true;
}

export const useUniversalCreateTeamsItems = (): UseUniversalCreateItemsProps => {
	const intl = useIntl();
	const { teamCreate, goalCreate, kudosCreate } = useUniversalCreateItems();
	const userHasGoalsAccess = useUserHasCapabilities(['goals_app:access'], true);
	const showKudos = useShowKudos();

	const suggestionItems = [userHasGoalsAccess ? goalCreate : undefined].filter(filterUndefined);

	const teamsAppItems = useMemo<UniversalCreateMenuSection[]>(() => {
		const sections: UniversalCreateMenuSection[] = [];
		const primaryItems = [teamCreate, showKudos ? kudosCreate : undefined].filter(filterUndefined);

		if (primaryItems.length > 0) {
			sections.push({
				type: 'appDefinedSection' as const,
				items: primaryItems,
			});
		}

		if (suggestionItems.length > 0) {
			sections.push({
				type: 'appDefinedSection' as const,
				heading: intl.formatMessage({
					id: 'townsquare.navigation.universal-create-button.section-title',
					description: 'Displays the other create options',
					defaultMessage: 'Suggestions',
				}),
				items: suggestionItems,
			});
		}

		return sections;
	}, [intl, kudosCreate, showKudos, teamCreate, suggestionItems]);

	return { items: teamsAppItems, compact: !!suggestionItems.length };
};
